import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "./Reveal";
import { COMMUNITY } from "../../lib/content";

function CommunityRow({ item, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const reversed = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`community-row-${index}`}
      className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center py-14 md:py-20 border-t border-[#d4af37]/12"
    >
      {/* Image */}
      <div className={`lg:col-span-7 ${reversed ? "lg:order-2" : "lg:order-1"}`}>
        <Reveal>
          <div className="relative gold-border p-3 overflow-hidden group">
            <div className="overflow-hidden">
              <motion.img
                style={{ y: imgY }}
                src={item.image}
                alt={item.title}
                className="w-full h-[300px] md:h-[460px] object-cover scale-110 spotlight transition-transform duration-[1400ms] ease-out group-hover:scale-[1.18]"
              />
            </div>
            <span className="absolute top-6 left-6 font-serif-lux text-6xl md:text-7xl text-[#f5f5f0] drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
              {item.no}
            </span>
          </div>
        </Reveal>
      </div>

      {/* Text */}
      <div className={`lg:col-span-5 ${reversed ? "lg:order-1" : "lg:order-2"}`}>
        <Reveal delay={0.12}>
          <span className="text-xs tracking-luxe uppercase text-[#d4af37]">
            {item.kicker}
          </span>
          <h3 className="font-serif-lux text-3xl md:text-5xl mt-4 leading-[1.05] text-[#f5f5f0]">
            {item.title}
          </h3>
          <p className="mt-5 text-[#9a968c] leading-relaxed max-w-md">
            {item.desc}
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            {item.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-[#d4af37]/25 px-4 py-2 text-[11px] tracking-[0.12em] uppercase text-[#c9c5ba]"
              >
                {t}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  );
}

export default function Community() {
  return (
    <section
      id="community"
      data-testid="community"
      className="relative bg-[#070707] py-28 md:py-40 border-y border-[#d4af37]/10 scroll-mt-20 overflow-hidden"
    >
      {/* soft gold glow accents */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#d4af37]/[0.06] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#d4af37]/[0.05] blur-3xl" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal>
            <span className="text-xs tracking-luxe uppercase text-[#d4af37]">
              What Sets Sarrgosa Apart
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif-lux text-4xl md:text-6xl lg:text-7xl mt-6 leading-[1.02] text-[#f5f5f0]">
              A conscious, happy{" "}
              <span className="italic gold-gradient-text">community.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#9a968c] text-base lg:text-lg leading-relaxed max-w-xl">
              More than homes, Sarrgosa is a way of living well together — four
              signature spaces designed to heal the mind, spark ideas, empower
              work and bring neighbours to one warm table.
            </p>
          </Reveal>
        </div>

        <div className="mt-14">
          {COMMUNITY.map((item, i) => (
            <CommunityRow key={item.no} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
